import { z } from "zod";

/// URL search params for the attendance calendar.
///
/// Month and selected day live in the URL rather than component state so a
/// refresh keeps your place and a specific day can be linked to, which is what
/// someone does when they want to ask a colleague about one.
export const attendanceRouteSearchSchema = z
  .object({
    /// "YYYY-MM". Defaults to the current month.
    month: z
      .string()
      .regex(/^\d{4}-\d{2}$/)
      .optional(),
    /// "YYYY-MM-DD". Defaults to today.
    date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    /// Calendar for shape, list for detail. In the URL so a preference
    /// survives a reload rather than resetting every visit.
    view: z.enum(["calendar", "list"]).optional(),
    /// Which structural view is open. The page answers three different
    /// questions and stacking all of them answered none of them first.
    tab: z.enum(["month", "requests"]).optional(),
  })
  .strict();

export type AttendanceRouteSearch = z.infer<typeof attendanceRouteSearchSchema>;

/// Team and board pages: which day is on screen, plus the board's filters.
/// Shared by the manager team view and the admin board. The two differ only
/// in breadth, and breadth comes from the server rather than from the URL, so
/// they take the same filters: "who was absent yesterday" is as useful a
/// question for a manager over their own reports as for an admin over the
/// workspace.
///
/// `date` absent means "the most recent day that has records", resolved at
/// render time. It is deliberately not defaulted to today here: today never
/// has records, and a URL that said so would make the empty result look chosen.
export const attendanceDayRouteSearchSchema = z
  .object({
    date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    departmentId: z.string().optional(),
    status: z.string().optional(),
    /// Which exception the table is narrowed to, from the "needs attention"
    /// strip. In the URL because it is a view somebody shares or comes back to
    /// ("show me who is missing a check-out"), not transient component state.
    exception: z.enum(["unresolved", "missingCheckout", "late", "leftEarly"]).optional(),
    /// Free-text match on the employee name, handled by the shared table. In
    /// the URL so a narrowed view survives a reload and can be shared.
    q: z.string().optional(),
    /// Which structural view is open. Reading a day and deciding a request are
    /// different jobs for the same person.
    tab: z.enum(["day", "people", "requests", "register"]).optional(),
    /// The teammate whose month the People tab shows. A membership id; the
    /// server narrows it to the caller's scope, so editing it widens nothing.
    member: z.string().optional(),
    /// Calendar or list on the People tab, as on My attendance.
    view: z.enum(["calendar", "list"]).optional(),
    /// A pending regularisation opened from payroll review. The Requests tab
    /// uses it to open the existing decision dialog for that exact request.
    requestId: z.string().min(1).optional(),
    /// "YYYY-MM" for the register tab. Its own param rather than derived from
    /// `date`, which is what tied the register's month to the day board's
    /// cursor: changing month meant leaving the tab, paging the day back, and
    /// coming back.
    month: z
      .string()
      .regex(/^\d{4}-\d{2}$/)
      .optional(),
    returnTo: z
      .string()
      .max(2048)
      .regex(/^\/(?!\/)/)
      .optional(),
  })
  .strict();
